import React, { useEffect, useState, useMemo } from "react";
import { Typography, TableSortLabel, TablePagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useGetAllCompanies } from "../../hooks/api/useCompanies";
import type { Company } from "../../Graphql/types/company";
import { getCompanyDetailPath } from "../../constants/routes";
import AdminScopeFilters, {
  ALL_BUSINESS_TYPES,
  ALL_COUNTRIES,
} from "../common/AdminScopeFilters";
import AdminActiveFilterChips from "../common/AdminActiveFilterChips";
import AdminPageShell from "../common/AdminPageShell";
import AdminFilterBar from "../common/AdminFilterBar";
import AdminSearchField from "../common/AdminSearchField";
import AdminDataTable, {
  type AdminTableColumn,
} from "../common/AdminDataTable";
import { ADMIN_MARKET_COUNTRIES } from "../common/adminMarketCountries";
import {
  formatBusinessTypeLabel,
  formatCompanyStatusLabel,
} from "../common/adminEnumLabels";

type OrderBy = "name" | "status" | "country" | "businessType" | "district";
type Order = "asc" | "desc";

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

function getSortValue(c: Company, field: OrderBy): string {
  if (field === "name") return c.name;
  if (field === "status") return c.status;
  if (field === "country") return c.address?.country ?? "";
  if (field === "businessType") return c.businessType ?? "";
  return c.address?.district ?? "";
}

function filterAndSort(
  list: Company[],
  search: string,
  businessType: string,
  orderBy: OrderBy,
  order: Order,
): Company[] {
  const term = search.trim().toLowerCase();
  let filtered = [...list];
  if (term) {
    filtered = filtered.filter((c) => c.name.toLowerCase().includes(term));
  }
  if (businessType) {
    filtered = filtered.filter((c) => c.businessType === businessType);
  }
  return filtered.sort((a, b) => {
    const aVal = getSortValue(a, orderBy);
    const bVal = getSortValue(b, orderBy);
    const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: "base" });
    return order === "asc" ? cmp : -cmp;
  });
}

const SORTABLE_COLUMNS: Record<string, OrderBy> = {
  name: "name",
  status: "status",
  country: "country",
  businessType: "businessType",
  district: "district",
};

const CompanyListView: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [search, setSearch] = useState("");
  const [countryCode, setCountryCode] = useState(ALL_COUNTRIES);
  const [businessType, setBusinessType] = useState(ALL_BUSINESS_TYPES);
  const [orderBy, setOrderBy] = useState<OrderBy>("name");
  const [order, setOrder] = useState<Order>("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getAllCompanies = useGetAllCompanies();

  useEffect(() => {
    const input = countryCode !== ALL_COUNTRIES ? { countryCode } : undefined;
    getAllCompanies
      .mutateAsync(input)
      .then((res) => {
        const list = res.getAllCompaniesSuperAdmin.companies ?? [];
        setCompanies(list);
      })
      .catch(() => {});
  }, [countryCode]);

  const sorted = useMemo(
    () => filterAndSort(companies, search, businessType, orderBy, order),
    [companies, search, businessType, orderBy, order],
  );

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (field: OrderBy) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const isLoading = getAllCompanies.isPending && companies.length === 0;
  const isEmpty = !isLoading && companies.length === 0;
  const noSearchResults = !isEmpty && sorted.length === 0;

  const countryLabel =
    countryCode !== ALL_COUNTRIES
      ? ADMIN_MARKET_COUNTRIES.find((c) => c.code === countryCode)?.label
      : null;

  const metaText = isLoading
    ? "Loading…"
    : isEmpty
      ? countryLabel
        ? `No companies in ${countryLabel}`
        : "No companies yet"
      : search.trim() || businessType
        ? `${sorted.length} of ${companies.length} ${companies.length === 1 ? "company" : "companies"}`
        : `${companies.length} ${companies.length === 1 ? "company" : "companies"}${countryLabel ? ` in ${countryLabel}` : ""}`;

  const columns: AdminTableColumn<Company>[] = [
    {
      id: "name",
      header: "Name",
      mobilePrimary: true,
      renderCell: (c) => (
        <Typography variant="body2" fontWeight={600}>
          {c.name}
        </Typography>
      ),
    },
    {
      id: "status",
      header: "Status",
      renderCell: (c) => (
        <Typography variant="body2" color="text.secondary">
          {formatCompanyStatusLabel(c.status)}
        </Typography>
      ),
    },
    {
      id: "country",
      header: "Country",
      renderCell: (c) => c.address?.country ?? "—",
    },
    {
      id: "businessType",
      header: "Industry",
      renderCell: (c) => formatBusinessTypeLabel(c.businessType),
    },
    {
      id: "district",
      header: "District",
      hideOnMobile: true,
      renderCell: (c) => c.address?.district ?? "—",
    },
  ];

  const emptyMessage = noSearchResults
    ? "No companies match the current filters."
    : "No companies found.";

  return (
    <AdminPageShell
      title="Companies"
      description="Browse tenant companies by registered market and industry."
      meta={metaText}
      toolbar={
        <AdminFilterBar
          search={
            <AdminSearchField
              placeholder="Search by company name…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
          }
          filters={
            <AdminScopeFilters
              values={{ countryCode, businessType }}
              onChange={({ countryCode: c, businessType: b }) => {
                setCountryCode(c);
                setBusinessType(b);
                setPage(0);
              }}
            />
          }
        />
      }
      filterChips={
        <AdminActiveFilterChips
          values={{ countryCode, businessType }}
          onClearCountry={() => {
            setCountryCode(ALL_COUNTRIES);
            setPage(0);
          }}
          onClearBusinessType={() => {
            setBusinessType(ALL_BUSINESS_TYPES);
            setPage(0);
          }}
          onClearAll={() => {
            setCountryCode(ALL_COUNTRIES);
            setBusinessType(ALL_BUSINESS_TYPES);
            setPage(0);
          }}
        />
      }
    >
      <AdminDataTable
        columns={columns}
        rows={paginated}
        getRowId={(c) => c.id}
        onRowClick={(c) => navigate(getCompanyDetailPath(c.id))}
        emptyMessage={emptyMessage}
        loading={isLoading}
        renderHeaderCell={(col) => {
          const sortField = SORTABLE_COLUMNS[col.id];
          if (!sortField) return col.header;
          return (
            <TableSortLabel
              active={orderBy === sortField}
              direction={orderBy === sortField ? order : "asc"}
              onClick={() => handleSort(sortField)}
            >
              {col.header}
            </TableSortLabel>
          );
        }}
        footer={
          !isLoading && !isEmpty && sorted.length > 0 ? (
            <TablePagination
              component="div"
              count={sorted.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Rows:"
              sx={{ borderTop: 1, borderColor: "divider" }}
            />
          ) : undefined
        }
      />
    </AdminPageShell>
  );
};

export default CompanyListView;
